import React, { Component } from "react";
import styled from "styled-components";
import { Menu, MenuItem, Tooltip } from "@material-ui/core";

import {
    PlayCircleFilled as PlayCircleFilledIcon,
    Stop as StopIcon
} from "@material-ui/icons";

import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import { _ } from "js/vendor";
import { SnapLineBindingPoint, AnimationsArrangementType, MIN_ANIMATION_DURATION_MS } from "common/constants";
import * as geom from "js/core/utilities/geom";
import { AnchorType } from "js/core/utilities/geom";
import { themeColors } from "js/react/sharedStyles";
import { BlueButton } from "js/react/components/UiComponents";
import { Key } from "js/core/utilities/keys";

import { ScrollContainer } from "js/react/views/AnimationPanelOld/Components/AnimationEditor/ScrollContainer";
import { Ruler } from "./Ruler";
import { AnimationHeader } from "./AnimationHeader";
import { AnimationBox } from "./AnimationBox";
import { AudioLine } from "./AudioLine";
import { AnimationPresetsMenu } from "./AnimationPresetsMenu";

const DragType = {
    DRAG_SELECTION: "dragSelection",
    DRAG_BOX: "dragBox",
    DRAG_LEFT_HANDLE: "dragLeftHandle",
    DRAG_RIGHT_HANDLE: "dragRightHandle",
    PEEK: "PEEK"
};

const SNAP_TOLERANCE = 0.005;

const RULER_TICK_SIZE_MS = 100;

const TargetType = {
    CONTAINER: "container",
    ANIMATION: "animation",
    RESIZE_HANDLE_LEFT: "resizeHandleLeft",
    RESIZE_HANDLE_RIGHT: "resizeHandleRight"
};

const TimelinePanelContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
    pointer-events: ${({ isReadOnly }) => isReadOnly ? "none" : "auto"};
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    align-items: flex-start;
`;

const HeaderButtonsContainer = styled.div`
    width: 280px;
    height: 80px;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
`;

const HeaderAudioLineAndRulerContainer = styled.div`
    width: 100%;
    height: 80px;
    display: flex;
    flex-flow: column;
`;

const TimelineContainer = styled.div`
    width: 100%;
    display: flex;
    flex-flow: row;
`;

const TimelineHeader = styled.div`
    width: 280px;
    height: 100%;
`;

const Timeline = styled.div`
    width: 100%;
    position: relative;
`;

const AnimationLine = styled.div.attrs(({ hilited }) => ({
    style: {
        backgroundColor: hilited ? "#f1f1f1" : "#ececec"
    }
}))`
        width: 100%;
        height: 30px;
        position: relative;
        border-top: 1px solid #999;
`;

const SelectionBoxTimeLabel = styled.span.attrs(({ position = "left", visible }) => ({
    style: {
        [position]: "-2px",
        opacity: visible ? 1 : 0
    }
}))`
        top: -2px;
        transform: translateY(-100%);
        position: absolute;
        font-size: 12px;
        white-space: nowrap;
        padding: 2px;
        background-color: #f9f9f9;
        border: 1px solid #a9a9a9;
        transition: opacity 0.5s;
`;

const SelectionBox = styled.div.attrs(({ left, top, width, height, interactive }) => ({
    style: {
        left: `${left * 100}%`,
        top: `${top * 100}%`,
        width: `${width * 100}%`,
        height: `${height * 100}%`,
        cursor: interactive ? "pointer" : "default"
    }
}))`
        outline: dotted 1px ${themeColors.ui_blue};
        position: absolute;
        pointer-events: none;
`;

const ResizeHandle = styled.div`
        cursor: ew-resize;
        position: absolute;
        border: solid 1px #6c6c6c;
        border-radius: 2px;
        background: white;
        width: 8px;
        height: 8px;
        top: calc(50% - 4px);
        pointer-events: auto;
`;

const AnimateButtonContainer = styled.div`
        width: 100%;
        height: 40px;
        padding-right: 10px;
`;

const PresetsButtonContainer = styled.div`
        width: 100%;
        height: 40px;
        padding-right: 10px;
        display: flex;
        align-items: center;
`;

const WaitForClickLine = styled.div.attrs(({ start, animationsDuration, disabled }) => ({
    style: {
        left: `calc(${start / animationsDuration * 100}% - 1px)`,
        opacity: disabled ? 0.3 : 1
    }
}))`
        position: absolute;
        width: 0px;
        height: 100%;
        top: 0;
        border-right: 2px dashed #00000073;
`;

const SnapLine = styled.div.attrs(({ left, top, height, width }) => ({
    style: {
        left: `${left * 100}%`,
        top: `${top * 100}%`,
        height: `${height * 100}%`,
        width: `${width * 100}%`
    }
}))`
        position: absolute;
        outline: dashed 1px #ec407a75;
`;

export class AnimationEditor extends Component {
    constructor() {
        super();

        this.state = {
            animations: [],
            hasAudio: false,
            selectedAnimationsIds: [],
            isDragging: false,
            dragSelectionBounds: null,
            animationProgress: 0,
            isAnimating: false,
            isContextMenuOpen: false,
            contextMenuPosition: null,
            arrangementType: AnimationsArrangementType.OVERLAPPING,
            migrated: false,
            snapLines: []
        };

        this.dragStartPosition = null;
        this.dragType = null;
        this.selectedAnimationsBeforeDrag = null;
        this.animationsDurationBeforeDrag = null;
        this.isMouseInsideContainer = false;

        this.rulerRef = React.createRef();
        this.timelineRef = React.createRef();
    }

    get timelineBounds() {
        if (!this.timelineRef.current) {
            return null;
        }

        return geom.Rect.FromBoundingClientRect(this.timelineRef.current.getBoundingClientRect());
    }

    get rulerBounds() {
        if (!this.timelineRef.current) {
            return null;
        }

        return geom.Rect.FromBoundingClientRect(this.rulerRef.current.getBoundingClientRect());
    }

    get normalizedTimelineBounds() {
        return new geom.Rect(0, 0, 1, 1);
    }

    get selectedAnimationsBounds() {
        let bounds;
        for (const animation of this.selectedAnimations) {
            if (!bounds) {
                bounds = this.getAnimationBounds(animation);
            } else {
                bounds = bounds.union(this.getAnimationBounds(animation));
            }
        }
        return bounds;
    }

    get selectedAnimations() {
        const { animations, selectedAnimationsIds } = this.state;
        return animations.filter(({ id }) => selectedAnimationsIds.includes(id));
    }

    get selectedAnimationsBeforeDragBounds() {
        let bounds;
        for (const animation of this.selectedAnimationsBeforeDrag) {
            if (!bounds) {
                bounds = this.getAnimationBounds(animation);
            } else {
                bounds = bounds.union(this.getAnimationBounds(animation));
            }
        }
        return bounds;
    }

    get selectedAnimationsElements() {
        return this.selectedAnimations.map(({ element }) => element);
    }

    get animationsDuration() {
        const { animations } = this.state;
        return _.max(animations.map(({ start, duration }) => start + duration));
    }

    getAnimation = animationId => {
        const { animations } = this.state;
        return animations.find(({ id }) => id === animationId);
    }

    getPositionFromEvent = event => {
        const containerBounds = this.timelineBounds;
        return new geom.Point((event.pageX - containerBounds.left) / containerBounds.width, (event.pageY - containerBounds.top) / containerBounds.height);
    }

    getAnimationBounds = animation => {
        const { animations } = this.state;

        const height = 1 / animations.length;
        const top = animation.index * height;

        const animationsDuration = this.animationsDuration;
        return new geom.Rect(animation.start / animationsDuration, top, animation.duration / animationsDuration, height);
    }

    getAnimationsBounds = animations => {
        let bounds;
        for (const animation of animations) {
            if (!bounds) {
                bounds = this.getAnimationBounds(animation);
            } else {
                bounds = bounds.union(this.getAnimationBounds(animation));
            }
        }

        return bounds;
    }

    fetchAnimations = async () => {
        const { canvas } = this.props;
        const { selectedAnimationsIds } = this.state;

        // If canvas is generating then we have to wait for it because some elements
        // can't calculate their animations until they render (i.e. Chart)
        if (canvas.layouter.isGenerating) {
            await new Promise(resolve => canvas.layouter.runPostRender(resolve));
        }

        const animations = canvas.getAnimations();
        return this.setStateAsync({
            animations,
            hasAudio: canvas.hasAudio(),
            selectedAnimationsIds: selectedAnimationsIds.filter(id => animations.some(animation => animation.id === id)),
            ...canvas.getGeneralAnimationsSettings()
        });
    }

    setStateAsync(stateUpdates) {
        return new Promise(resolve => this.setState(stateUpdates, resolve));
    }

    getSnapLinesForBounds = (bounds, bindingPoints = [SnapLineBindingPoint.START, SnapLineBindingPoint.END]) => [
        {
            bindingPoint: SnapLineBindingPoint.START,
            corridorBounds: new geom.Rect(bounds.left - SNAP_TOLERANCE / 2, 0, SNAP_TOLERANCE, this.normalizedTimelineBounds.height),
            snapLineBounds: new geom.Rect(bounds.left, this.normalizedTimelineBounds.top, 0, this.normalizedTimelineBounds.height)
        },
        {
            bindingPoint: SnapLineBindingPoint.END,
            corridorBounds: new geom.Rect(bounds.right - SNAP_TOLERANCE / 2, 0, SNAP_TOLERANCE, this.normalizedTimelineBounds.height),
            snapLineBounds: new geom.Rect(bounds.right, this.normalizedTimelineBounds.top, 0, this.normalizedTimelineBounds.height)
        }
    ]
        .filter(snapLine => bindingPoints.includes(snapLine.bindingPoint))
        // Adding source bounds to each snap line
        .map(snapLine => ({ ...snapLine, sourceBounds: bounds }))

    getAnimationsSnapLines = (fromAnimations, unionBounds, bindingPoints = [SnapLineBindingPoint.START, SnapLineBindingPoint.END]) => {
        if (unionBounds) {
            const bounds = this.getAnimationsBounds(fromAnimations);
            return this.getSnapLinesForBounds(bounds, bindingPoints);
        }

        const snapLines = [];
        for (const animation of fromAnimations) {
            const bounds = this.getAnimationBounds(animation);
            snapLines.push(...this.getSnapLinesForBounds(bounds, bindingPoints));
        }
        return snapLines;
    }

    getRulerSnapLines = animationsDuration => {
        const snapLines = [];
        let tickMs = 0;
        while (tickMs < animationsDuration) {
            const bounds = new geom.Rect(tickMs / animationsDuration, 0, RULER_TICK_SIZE_MS * 5 / animationsDuration, 1);
            snapLines.push(...this.getSnapLinesForBounds(bounds));

            tickMs += RULER_TICK_SIZE_MS * 5;
        }
        return snapLines;
    }

    calcSnap = (sourceSnapLines, destinationSnapLines) => {
        const snapLines = [];
        for (const sourceSnapLine of sourceSnapLines) {
            for (const destinationSnapLine of destinationSnapLines) {
                if (sourceSnapLine.corridorBounds.intersects(destinationSnapLine.corridorBounds)) {
                    // Enriching snap line with offset in order to reposition selected elements in future
                    // Also replacing bindingPoint with the binding point from the selection, will be used
                    // for calculating the correct offset
                    const offset = destinationSnapLine.snapLineBounds.x - sourceSnapLine.snapLineBounds.x;
                    const calculatedSnapLine = { ...destinationSnapLine, bindingPoint: sourceSnapLine.bindingPoint, offset };

                    // Cutting snap line
                    const emitterAndReceiverBounds = sourceSnapLine.sourceBounds.union(destinationSnapLine.sourceBounds);
                    calculatedSnapLine.snapLineBounds = calculatedSnapLine.snapLineBounds.intersection(emitterAndReceiverBounds);

                    snapLines.push(calculatedSnapLine);
                }
            }
        }

        if (snapLines.length === 0) {
            return null;
        }

        // We want to apply only the closest horizontal and vertical snap lines (one of each type)
        const sortedSnapLines = snapLines.sort((a, b) => a.offset - b.offset);
        return sortedSnapLines[0];
    }

    componentDidMount() {
        const { canvas } = this.props;

        window.addEventListener("mouseup", this.onMouseUp);
        window.addEventListener("mousemove", this.onMouseMove);
        window.addEventListener("keydown", this.onKeyDown);

        canvas.dataModel.on("change", this.fetchAnimations);

        ds.selection.on("change:element change:authoringElements", this.onSelectedElementsChange);

        this.fetchAnimations()
            // Force update to let the progress mark refresh with the correct timeline height
            .then(() => this.forceUpdate());
    }

    componentWillUnmount() {
        const { canvas } = this.props;

        window.removeEventListener("mouseup", this.onMouseUp);
        window.removeEventListener("mousemove", this.onMouseMove);
        window.removeEventListener("keydown", this.onKeyDown);

        canvas.dataModel.off("change", this.fetchAnimations);

        ds.selection.off("change:element change:authoringElements", this.onSelectedElementsChange);

        // Will have to check if the canvas still has layouter because if the slide
        // was deleted then then the layouter may have been disposed already
        if (canvas.layouter) {
            canvas.dimHilitedElements();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { canvas } = this.props;
        const { isAnimating, selectedAnimationsIds } = this.state;

        if (
            prevState.selectedAnimationsIds.length !== selectedAnimationsIds.length ||
            prevState.selectedAnimationsIds.some(prevId => !selectedAnimationsIds.includes(prevId)) ||
            selectedAnimationsIds.some(id => !prevState.selectedAnimationsIds.includes(id))
        ) {
            if (this.isMouseInsideContainer) {
                if (selectedAnimationsIds.length > 0) {
                    canvas.hiliteElements(this.selectedAnimationsElements);
                } else {
                    canvas.dimHilitedElements();
                }
            }
        } else if (isAnimating !== prevState.isAnimating) {
            if (isAnimating) {
                canvas.dimHilitedElements();
            } else if (selectedAnimationsIds.length > 0) {
                canvas.hiliteElements(this.selectedAnimationsElements);
            }
        }
    }

    onSelectedElementsChange = ({ element, authoringElements }) => {
        const { animations } = this.state;

        const elementsAreRelated = (elementA, elementB) =>
            elementA === elementB ||
            elementA.isChildOf(elementB) ||
            elementB.isChildOf(elementA);

        if (authoringElements.length > 0) {
            const selectedAnimationsIds = animations
                .filter(animation => authoringElements.some(authoringElement => elementsAreRelated(animation.element, authoringElement)))
                .map(({ id }) => id);

            this.setState({ selectedAnimationsIds });
        } else if (element) {
            const selectedAnimationsIds = animations
                .filter(animation => elementsAreRelated(animation.element, element))
                .map(({ id }) => id);

            this.setState({ selectedAnimationsIds });
        }
    }

    onKeyDown = async event => {
        const { selectedAnimationsIds, animations } = this.state;
        if (selectedAnimationsIds.length === 0) {
            return;
        }

        const key = event.which;
        if ([Key.RIGHT_ARROW, Key.LEFT_ARROW].includes(key)) {
            event.stopPropagation();
            event.preventDefault();

            let offset = key === Key.RIGHT_ARROW ? 10 : -10; // 10ms
            if (event.shiftKey) {
                offset *= 10; // 100ms with shift
            }
            this.selectedAnimations.forEach(({ start }) => {
                offset = Math.max(offset, -start);
            });
            if (offset === 0) {
                return;
            }

            this.selectedAnimations.forEach(animation => {
                animation.start += offset;
            });

            await this.setStateAsync({ animations });

            await this.syncAnimations();
        }
    }

    onMouseDown = async (event, targetType, target = null, preventDrag = false) => {
        if (![0, 2].includes(event.button)) {
            return;
        }

        event.stopPropagation();
        event.persist();

        const { isAnimating, selectedAnimationsIds } = this.state;
        if (isAnimating) {
            return;
        }

        if (targetType === TargetType.ANIMATION) {
            if (!selectedAnimationsIds.some(id => target.id === id)) {
                if (event.shiftKey) {
                    await this.setStateAsync({ selectedAnimationsIds: [...selectedAnimationsIds, target.id] });
                } else {
                    await this.setStateAsync({ selectedAnimationsIds: [target.id] });
                }
            } else if (event.shiftKey) {
                await this.setStateAsync({ selectedAnimationsIds: selectedAnimationsIds.filter(id => id !== target.id) });
            }

            if (!event.shiftKey && event.button === 0 && !preventDrag) {
                this.onStartDrag(event, DragType.DRAG_BOX);
            } else if (event.button === 2) {
                this.showContextMenu(event);
            }
            return;
        }

        if (targetType === TargetType.RESIZE_HANDLE_LEFT || targetType === TargetType.RESIZE_HANDLE_RIGHT) {
            if (event.button === 0) {
                this.onStartDrag(event, targetType === TargetType.RESIZE_HANDLE_LEFT ? DragType.DRAG_LEFT_HANDLE : DragType.DRAG_RIGHT_HANDLE);
            } else {
                this.showContextMenu(event);
            }
            return;
        }

        if (targetType === TargetType.CONTAINER) {
            if (selectedAnimationsIds.length > 0) {
                await this.setStateAsync({ selectedAnimationsIds: [] });
            }

            if (event.button === 0) {
                this.onStartDrag(event, DragType.DRAG_SELECTION);
            } else {
                this.showContextMenu(event);
            }
            return;
        }
    }

    onStartDrag = (event, dragType) => {
        if (dragType !== DragType.DRAG_SELECTION && dragType !== DragType.PEEK) {
            this.selectedAnimationsBeforeDrag = _.cloneDeep(this.selectedAnimations.map(({ start, duration, id, index }) => ({ start, duration, id, index })));
            this.animationsDurationBeforeDrag = this.animationsDuration;
        }
        this.dragStartPosition = this.getPositionFromEvent(event);
        this.dragType = dragType;

        this.setState({ isDragging: true });
    }

    onMouseUp = async () => {
        const { canvas } = this.props;
        const { isDragging } = this.state;
        if (!isDragging) {
            return;
        }

        const stateUpdates = {
            isDragging: false,
            dragSelectionBounds: null,
            snapLines: []
        };

        if (this.dragType === DragType.PEEK) {
            stateUpdates.animationProgress = 0;
            stateUpdates.isAnimating = false;
            await canvas.finishAnimation();
            app.mainView.editorView.showSelectionLayer();
        }

        await this.setStateAsync(stateUpdates);

        if (this.dragType !== DragType.DRAG_SELECTION && this.dragType !== DragType.PEEK) {
            await this.syncAnimations();
        }

        this.selectedAnimationsBeforeDrag = null;
        this.animationsDurationBeforeDrag = null;
        this.dragStartPosition = null;
        this.dragType = null;
    }

    onMouseMove = event => {
        const { canvas } = this.props;
        const { isDragging, animations, selectedAnimationsIds } = this.state;
        if (!isDragging) {
            return;
        }

        const dragPosition = this.getPositionFromEvent(event);

        if (this.dragType === DragType.PEEK) {
            const animationProgress = Math.clamp(dragPosition.x - this.dragStartPosition.x, 0, 1) * this.animationsDuration;
            this.setState({ animationProgress });
            canvas.renderAnimationState(animationProgress);
            return;
        }

        if (this.dragType === DragType.DRAG_SELECTION) {
            const dragSelectionBounds = new geom.Rect(this.dragStartPosition.x, this.dragStartPosition.y, dragPosition.x - this.dragStartPosition.x, dragPosition.y - this.dragStartPosition.y)
                .normalize()
                .intersection(this.normalizedTimelineBounds);

            const selectedAnimationsIds = animations.filter(animation => this.getAnimationBounds(animation).intersects(dragSelectionBounds)).map(({ id }) => id);

            this.setState({ dragSelectionBounds, selectedAnimationsIds });
            return;
        }

        const animationsDuration = this.animationsDuration;

        const otherAnimationsSnapLines = this.getAnimationsSnapLines(animations.filter(({ id }) => !selectedAnimationsIds.includes(id)));
        const rulerSnapLines = this.getRulerSnapLines(animationsDuration);
        const destinationSnapLines = [...otherAnimationsSnapLines, ...rulerSnapLines];

        if (this.dragType === DragType.DRAG_BOX) {
            let offset = dragPosition.minus(this.dragStartPosition).x * animationsDuration;
            this.selectedAnimationsBeforeDrag.forEach(({ start }) => {
                offset = Math.max(offset, -start);
            });

            this.selectedAnimations.forEach(animation => {
                animation.start = this.selectedAnimationsBeforeDrag.find(({ id }) => id === animation.id).start + offset;
            });

            const selectionSnapLines = this.getAnimationsSnapLines(this.selectedAnimations, true);
            const snapLine = this.calcSnap(selectionSnapLines, destinationSnapLines);
            const snapLines = [];
            if (snapLine) {
                snapLines.push(snapLine);
                this.selectedAnimations.forEach(animation => {
                    const offset = Math.max(snapLine.offset * animationsDuration, -animation.start);
                    animation.start += offset;
                });
            }

            this.setState({ animations, snapLines });
            return;
        }

        if ([DragType.DRAG_LEFT_HANDLE, DragType.DRAG_RIGHT_HANDLE].includes(this.dragType)) {
            let xScale, scalingAnchorPointX;
            const selectedAnimationsBounds = this.selectedAnimationsBeforeDragBounds;

            const calcDragResize = dragPosition => {
                if (this.dragType === DragType.DRAG_LEFT_HANDLE) {
                    scalingAnchorPointX = selectedAnimationsBounds.getPoint(AnchorType.RIGHT).x * animationsDuration;

                    const newWidth = selectedAnimationsBounds.left + selectedAnimationsBounds.width - dragPosition.x;
                    xScale = newWidth / selectedAnimationsBounds.width;
                    this.selectedAnimationsBeforeDrag.forEach(({ start, duration }) => {
                        xScale = Math.clamp(xScale, MIN_ANIMATION_DURATION_MS / duration, -scalingAnchorPointX / (start - scalingAnchorPointX));
                    });
                } else if (this.dragType === DragType.DRAG_RIGHT_HANDLE) {
                    scalingAnchorPointX = selectedAnimationsBounds.getPoint(AnchorType.LEFT).x * animationsDuration;

                    const newWidth = dragPosition.x - selectedAnimationsBounds.left;
                    xScale = newWidth / selectedAnimationsBounds.width / animationsDuration * this.animationsDurationBeforeDrag;
                    this.selectedAnimationsBeforeDrag.forEach(({ duration }) => {
                        xScale = Math.max(xScale, MIN_ANIMATION_DURATION_MS / duration);
                    });
                }

                this.selectedAnimationsBeforeDrag.forEach(({ id, start, duration }) => {
                    const animation = this.getAnimation(id);
                    animation.start = scalingAnchorPointX + (start - scalingAnchorPointX) * xScale;
                    animation.duration = duration * xScale;
                });
            };

            calcDragResize(dragPosition);

            const bindingPoints = [this.dragType === DragType.DRAG_LEFT_HANDLE ? SnapLineBindingPoint.START : SnapLineBindingPoint.END];
            const selectionSnapLines = this.getAnimationsSnapLines(this.selectedAnimations, true, bindingPoints);
            const snapLine = this.calcSnap(selectionSnapLines, destinationSnapLines);
            const snapLines = [];
            if (snapLine) {
                snapLines.push(snapLine);
                calcDragResize(dragPosition.offset(snapLine.offset, 0));
            }

            this.setState({ animations, snapLines });
            return;
        }
    }

    onStartPeek = async event => {
        event.stopPropagation();
        event.persist();

        if (event.button !== 0) {
            return;
        }

        const { isAnimating } = this.state;
        if (isAnimating) {
            return;
        }

        app.mainView.editorView.hideSelectionLayer();

        await this.setStateAsync({ isAnimating: true });

        const { canvas } = this.props;
        await canvas.prepareForAnimation();

        this.onStartDrag(event, DragType.PEEK);
    }

    animate = async event => {
        event.stopPropagation();

        const { canvas } = this.props;

        app.mainView.editorView.hideSelectionLayer();

        await this.setStateAsync({ isAnimating: true });
        await canvas.animate(animationProgress => this.setState({ animationProgress }));

        if (this.selectedAnimations.length > 0) {
            canvas.hiliteElements(this.selectedAnimationsElements);
        }

        app.mainView.editorView.showSelectionLayer();

        this.setState({ isAnimating: false, animationProgress: 0 });
    }

    showContextMenu = event => {
        const positionInContainer = this.getPositionFromEvent(event);
        const contextMenuPosition = new geom.Point(this.timelineBounds.left + positionInContainer.x * this.timelineBounds.width, this.timelineBounds.top + positionInContainer.y * this.timelineBounds.height);
        this.setState({ isContextMenuOpen: true, contextMenuPosition });
    }

    closeContextMenu = event => {
        event.stopPropagation();

        this.setState({ isContextMenuOpen: false, contextMenuPosition: null });
    }

    selectAllAnimations = event => {
        event.stopPropagation();

        const { animations } = this.state;
        this.setState({ selectedAnimationsIds: animations.map(({ id }) => id), isContextMenuOpen: false, contextMenuPosition: null });
    }

    scaleAnimations = async (event, scale) => {
        event.stopPropagation();

        const { animations } = this.state;

        animations.forEach(animation => {
            animation.start *= scale;
            animation.duration = Math.max(animation.duration * scale, MIN_ANIMATION_DURATION_MS);
        });

        await this.setStateAsync({ animations });

        await this.syncAnimations();
    }

    arrangeAnimations = async (event, arrangementType, animationIds = null, normalizeStart = false, forceEnable = false, updateGeneralSettings = false) => {
        event.stopPropagation();

        const { animations } = this.state;
        const { canvas } = this.props;

        const animationsToArrange = animationIds ? animations.filter(({ id }) => animationIds.includes(id)) : animations;

        canvas.arrangeAnimations(animationsToArrange, arrangementType, normalizeStart);

        if (forceEnable) {
            animationsToArrange.forEach(animation => animation.disabled = false);
        }

        if (updateGeneralSettings) {
            canvas.updateGeneralAnimationsSettings({ arrangementType });
        }

        await this.setState({ animations });

        await this.syncAnimations();
    }

    setAnimationsValues = async (event, animationIds, values, saveChanges = true) => {
        event.stopPropagation();

        const { animations } = this.state;
        animations
            .filter(({ id }) => animationIds.includes(id))
            .forEach(animation => Object.entries(values).forEach(([key, value]) => animation[key] = value));

        await this.setStateAsync({ animations });

        await this.syncAnimations(saveChanges);
    }

    syncAnimations = async (saveChanges = true) => {
        const { canvas } = this.props;
        const { animations } = this.state;

        animations.forEach(animation => {
            canvas.updateAnimation(animation.id, animation);
        });

        if (saveChanges) {
            await canvas.saveCanvasModel();
        }
    }

    render() {
        const {
            canvas,
            isReadOnly
        } = this.props;

        const {
            hasAudio,
            isAnimating,
            animations,
            isDragging,
            animationProgress,
            dragSelectionBounds,
            selectedAnimationsIds,
            isContextMenuOpen,
            contextMenuPosition,
            snapLines,
            arrangementType
        } = this.state;

        const animationsWithWaitForClick = [];
        {
            const allAnimationsWithWaitForClick = animations.filter(({ waitForClick }) => waitForClick).map(({ start, disabled }) => ({ start, disabled }));
            allAnimationsWithWaitForClick.sort((a, b) => a.start - b.start);
            allAnimationsWithWaitForClick.forEach(animation => {
                if (!animationsWithWaitForClick.some(({ start }) => Math.abs(animation.start - start) < 0.01)) {
                    animationsWithWaitForClick.push(animation);
                }
            });
        }

        const elementAnimations = {};
        animations.forEach(animation => {
            if (!elementAnimations[animation.elementId]) {
                elementAnimations[animation.elementId] = { element: animation.element, animations: [animation] };
            } else {
                elementAnimations[animation.elementId].animations.push(animation);
            }
        });

        if (Object.keys(elementAnimations).length > 0) {
            _.last(_.last(Object.values(elementAnimations)).animations).isLast = true;
        }

        const animationsDuration = this.animationsDuration;

        const canPreview = (animations.some(({ disabled }) => !disabled) || hasAudio) && !isReadOnly;

        return (
            <TimelinePanelContainer
                isReadOnly={isReadOnly}
                onMouseDown={event => this.onMouseDown(event, TargetType.CONTAINER)}
                onMouseEnter={() => {
                    if (!isAnimating && selectedAnimationsIds.length > 0) {
                        canvas.hiliteElements(this.selectedAnimationsElements);
                    }
                    this.isMouseInsideContainer = true;
                }}
                onMouseLeave={() => {
                    if (!isAnimating && selectedAnimationsIds.length > 0) {
                        canvas.dimHilitedElements();
                    }
                    this.isMouseInsideContainer = false;
                }}
            >
                <Header>
                    <HeaderButtonsContainer>
                        <AnimateButtonContainer>
                            <BlueButton
                                style={{ height: "40px", pointerEvents: "auto" }}
                                disabled={!canPreview}
                                onMouseDown={this.animate}
                                startIcon={isAnimating ? <StopIcon /> : <PlayCircleFilledIcon />}
                                fullWidth
                                disableElevation
                            >
                                {isAnimating ? "Press ESC to stop" : "Preview"}
                            </BlueButton>
                        </AnimateButtonContainer>
                        <PresetsButtonContainer>
                            <AnimationPresetsMenu
                                animations={animations}
                                arrangeAnimations={this.arrangeAnimations}
                                setAnimationsValues={this.setAnimationsValues}
                                scaleAnimations={this.scaleAnimations}
                                arrangementType={arrangementType}
                                disabled={animations.length === 0 || isReadOnly}
                            />
                        </PresetsButtonContainer>
                    </HeaderButtonsContainer>
                    <HeaderAudioLineAndRulerContainer>
                        <AudioLine canvas={canvas} animationsDuration={animationsDuration} />
                        {this.timelineBounds && animations.length > 0 &&
                            <Ruler
                                ref={this.rulerRef}
                                animationsDuration={animationsDuration}
                                tickSizeMs={RULER_TICK_SIZE_MS}
                                progress={animationProgress}
                                onStartPeek={this.onStartPeek}
                                peekDisabled={isAnimating || !canPreview}
                                timelineHeight={this.timelineBounds.height}
                            />
                        }
                    </HeaderAudioLineAndRulerContainer>
                </Header>
                <ScrollContainer>
                    <TimelineContainer>
                        <TimelineHeader>
                            {Object.values(elementAnimations).map(({ element, animations }, idx) => (
                                <AnimationHeader
                                    key={idx}
                                    hilited={idx % 2 === 0}
                                    animations={animations}
                                    disabled={isAnimating}
                                    onMouseEnter={() => !isAnimating && selectedAnimationsIds.length === 0 && canvas.hiliteElements([element])}
                                    onMouseLeave={() => !isAnimating && selectedAnimationsIds.length === 0 && canvas.dimHilitedElements()}
                                    setAnimationsValues={this.setAnimationsValues}
                                    onMouseDown={event => this.onMouseDown(event)}
                                />
                            ))}
                        </TimelineHeader>
                        <Timeline ref={this.timelineRef}>
                            {Object.values(elementAnimations).map(({ element, animations: elementAnimations }, idx) =>
                                elementAnimations.map(animation => (
                                    <AnimationLine
                                        key={`${animation.id}${animation.index}`}
                                        hilited={idx % 2 === 0}
                                        onMouseEnter={() => !isAnimating && selectedAnimationsIds.length === 0 && canvas.hiliteElements([element])}
                                        onMouseLeave={() => !isAnimating && selectedAnimationsIds.length === 0 && canvas.dimHilitedElements()}
                                    >
                                        <AnimationBox
                                            animation={animation}
                                            animationsDuration={animationsDuration}
                                            selected={selectedAnimationsIds.some(id => id === animation.id)}
                                            name={animation.name}
                                            onMouseDown={(event, preventDrag) => this.onMouseDown(event, TargetType.ANIMATION, animation, preventDrag)}
                                            isLast={animation.isLast}
                                        />
                                    </AnimationLine>
                                ))
                            )}
                            {animationsWithWaitForClick.map(({ start, disabled }) => (
                                <Tooltip key={start} title="Wait for click" placement="top">
                                    <WaitForClickLine start={start} animationsDuration={animationsDuration} disabled={disabled} />
                                </Tooltip>
                            ))}
                            {snapLines.map(snapLine => (
                                <SnapLine {...snapLine.snapLineBounds.toObject()} />
                            ))}
                            {isDragging && this.dragType === DragType.DRAG_SELECTION && dragSelectionBounds &&
                                <SelectionBox {...dragSelectionBounds.toObject()} />
                            }
                            {selectedAnimationsIds.length > 0 &&
                                <SelectionBox
                                    {...this.selectedAnimationsBounds.toObject()}
                                    interactive
                                >
                                    {!isAnimating && <ResizeHandle
                                        style={{ left: "-4px" }}
                                        onMouseDown={event => this.onMouseDown(event, TargetType.RESIZE_HANDLE_LEFT)}
                                    />}
                                    {!isAnimating && <ResizeHandle
                                        style={{ right: "-4px" }}
                                        onMouseDown={event => this.onMouseDown(event, TargetType.RESIZE_HANDLE_RIGHT)}
                                    />}
                                    <SelectionBoxTimeLabel visible={isDragging} position="left">{(this.selectedAnimationsBounds.left * animationsDuration / 1000).toFixed(1)}s</SelectionBoxTimeLabel>
                                    <SelectionBoxTimeLabel visible={isDragging} position="right">{(this.selectedAnimationsBounds.right * animationsDuration / 1000).toFixed(1)}s</SelectionBoxTimeLabel>
                                </SelectionBox>
                            }
                            <Menu
                                open={isContextMenuOpen}
                                anchorReference="anchorPosition"
                                anchorPosition={{ left: contextMenuPosition?.x ?? 0, top: contextMenuPosition?.y ?? 0 }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                onClose={this.closeContextMenu}
                            >
                                {selectedAnimationsIds.length === 0 &&
                                    <MenuItem onMouseDown={this.selectAllAnimations}>
                                        Select All
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 1 &&
                                    <MenuItem onMouseDown={event => {
                                        this.arrangeAnimations(event, AnimationsArrangementType.SEQUENTIAL, selectedAnimationsIds);
                                        this.setState({ isContextMenuOpen: false });
                                    }}>
                                        Play sequentially
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 1 &&
                                    <MenuItem onMouseDown={event => {
                                        this.arrangeAnimations(event, AnimationsArrangementType.SIMULTANEOUS, selectedAnimationsIds);
                                        this.setState({ isContextMenuOpen: false });
                                    }}>
                                        Play simulteneously
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 1 &&
                                    <MenuItem onMouseDown={event => {
                                        this.arrangeAnimations(event, AnimationsArrangementType.OVERLAPPING, selectedAnimationsIds);
                                        this.setState({ isContextMenuOpen: false });
                                    }}>
                                        Play overlapping
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 0 && this.selectedAnimations.some(animation => !animation.disabled) &&
                                    <MenuItem onMouseDown={event => {
                                        this.setAnimationsValues(event, selectedAnimationsIds, { disabled: true });
                                        this.setState({ isContextMenuOpen: false });
                                    }}>
                                        Skip
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 0 && !this.selectedAnimations.some(animation => !animation.disabled) &&
                                    <MenuItem onMouseDown={event => {
                                        this.setAnimationsValues(event, selectedAnimationsIds, { disabled: false });
                                        this.setState({ isContextMenuOpen: false });
                                    }}>
                                        Unskip
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 0 && !this.selectedAnimations.some(animation => animation.waitForClick) &&
                                    <MenuItem onMouseDown={event => {
                                        this.setAnimationsValues(event, selectedAnimationsIds, { waitForClick: true });
                                        this.setState({ isContextMenuOpen: false });
                                    }}>
                                        Wait for click
                                    </MenuItem>
                                }
                                {selectedAnimationsIds.length > 0 && this.selectedAnimations.some(animation => animation.waitForClick) &&
                                    <MenuItem onMouseDown={event => {
                                        this.setAnimationsValues(event, selectedAnimationsIds, { waitForClick: false });
                                    }}>
                                        Remove wait for click
                                    </MenuItem>
                                }
                            </Menu>
                        </Timeline>
                    </TimelineContainer>
                </ScrollContainer>
            </TimelinePanelContainer>
        );
    }
}
